import { LitElement, html, css } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/divider/divider.js";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "../components/app-shell";
import { VERSION_INFO } from "../config/version";

@customElement("help-page")
export class HelpPage extends LitElement {
  @property({ type: Boolean, attribute: "guest-mode" }) guestMode = false;
  @state() private activeSection = "overview";

  static styles = css`
    :host {
      display: block;
      background: #f8f9fa;
      min-height: 100vh;
    }

    .help-container {
      max-width: 900px;
      margin: 0 auto;
      padding: 1rem;
    }

    .help-header {
      text-align: center;
      padding: 2rem 1rem;
      background: linear-gradient(135deg, #0d6efd 0%, #6610f2 100%);
      color: white;
      border-radius: 1rem;
      margin-bottom: 1.5rem;
    }

    .help-header h1 {
      margin: 0 0 0.5rem 0;
      font-size: 2rem;
    }

    .help-header p {
      margin: 0;
      opacity: 0.9;
    }

    /* Table of Contents */
    .toc {
      background: white;
      border-radius: 0.75rem;
      padding: 1rem;
      margin-bottom: 1.5rem;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    .toc h3 {
      margin: 0 0 0.75rem 0;
      font-size: 1rem;
      color: #6c757d;
    }

    .toc-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
      gap: 0.5rem;
    }

    .toc-item {
      padding: 0.5rem 0.75rem;
      border-radius: 0.5rem;
      cursor: pointer;
      transition: all 0.2s;
      font-size: 0.85rem;
      border: 2px solid transparent;
    }

    .toc-item:hover {
      transform: translateY(-1px);
    }

    .toc-item.active {
      border-color: currentColor;
    }

    /* Color-coded sections */
    .section {
      background: white;
      border-radius: 0.75rem;
      margin-bottom: 1rem;
      overflow: hidden;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    .section-header {
      padding: 1rem 1.25rem;
      color: white;
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .section-header h2 {
      margin: 0;
      font-size: 1.25rem;
    }

    .section-header wa-icon {
      font-size: 1.5rem;
    }

    .section-content {
      padding: 1.25rem;
    }

    /* Section colors */
    .section.overview .section-header {
      background: linear-gradient(135deg, #0d6efd, #6610f2);
    }
    .section.carbs .section-header {
      background: linear-gradient(135deg, #fd7e14, #e35d04);
    }
    .section.insulin .section-header {
      background: linear-gradient(135deg, #dc3545, #c82333);
    }
    .section.correction .section-header {
      background: linear-gradient(135deg, #6f42c1, #5a32a3);
    }
    .section.periods .section-header {
      background: linear-gradient(135deg, #198754, #157347);
    }
    .section.exercise .section-header {
      background: linear-gradient(135deg, #0dcaf0, #0aa2c0);
    }
    .section.hypo .section-header {
      background: linear-gradient(135deg, #ffc107, #e0a800);
    }
    .section.sick .section-header {
      background: linear-gradient(135deg, #6c757d, #545b62);
    }
    .section.notation .section-header {
      background: linear-gradient(135deg, #20c997, #1aa179);
    }
    .section.software .section-header {
      background: linear-gradient(135deg, #6366f1, #4f46e5);
    }

    /* TOC item colors */
    .toc-item.overview {
      background: #e7f1ff;
      color: #0d6efd;
    }
    .toc-item.carbs {
      background: #fff3e0;
      color: #fd7e14;
    }
    .toc-item.insulin {
      background: #f8d7da;
      color: #dc3545;
    }
    .toc-item.correction {
      background: #e8dcf5;
      color: #6f42c1;
    }
    .toc-item.periods {
      background: #d1e7dd;
      color: #198754;
    }
    .toc-item.exercise {
      background: #cff4fc;
      color: #0dcaf0;
    }
    .toc-item.hypo {
      background: #fff3cd;
      color: #856404;
    }
    .toc-item.sick {
      background: #e2e3e5;
      color: #6c757d;
    }
    .toc-item.notation {
      background: #d2f4ea;
      color: #0d9488;
    }
    .toc-item.software {
      background: #e0e7ff;
      color: #4f46e5;
    }
    .toc-item.about {
      background: #e9ecef;
      color: #495057;
    }

    /* Content styling */
    .key-point {
      background: #f8f9fa;
      border-left: 4px solid;
      padding: 0.75rem 1rem;
      margin: 0.75rem 0;
      border-radius: 0 0.5rem 0.5rem 0;
    }

    .key-point.blue {
      border-color: #0d6efd;
    }
    .key-point.orange {
      border-color: #fd7e14;
    }
    .key-point.red {
      border-color: #dc3545;
    }
    .key-point.purple {
      border-color: #6f42c1;
    }
    .key-point.green {
      border-color: #198754;
    }
    .key-point.cyan {
      border-color: #0dcaf0;
    }
    .key-point.yellow {
      border-color: #ffc107;
      background: #fffbeb;
    }
    .key-point.teal {
      border-color: #20c997;
    }

    .formula {
      background: #1e293b;
      color: #f1f5f9;
      padding: 1rem;
      border-radius: 0.5rem;
      font-family: monospace;
      margin: 0.75rem 0;
      font-size: 0.95rem;
    }

    .formula .highlight {
      color: #fbbf24;
    }

    .formula .comment {
      color: #94a3b8;
    }

    .example-box {
      background: #f0fdf4;
      border: 1px solid #86efac;
      padding: 1rem;
      border-radius: 0.5rem;
      margin: 0.75rem 0;
    }

    .example-box h4 {
      margin: 0 0 0.5rem 0;
      color: #166534;
      font-size: 0.9rem;
    }

    .warning-box {
      background: #fef2f2;
      border: 1px solid #fca5a5;
      padding: 1rem;
      border-radius: 0.5rem;
      margin: 0.75rem 0;
    }

    .warning-box h4 {
      margin: 0 0 0.5rem 0;
      color: #991b1b;
      font-size: 0.9rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .info-box {
      background: #eff6ff;
      border: 1px solid #93c5fd;
      padding: 1rem;
      border-radius: 0.5rem;
      margin: 0.75rem 0;
    }

    .info-box h4 {
      margin: 0 0 0.5rem 0;
      color: #1e40af;
      font-size: 0.9rem;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 0.75rem 0;
      font-size: 0.9rem;
    }

    th,
    td {
      padding: 0.75rem;
      text-align: left;
      border-bottom: 1px solid #e5e7eb;
    }

    th {
      background: #f8f9fa;
      font-weight: 600;
    }

    tr:hover {
      background: #f8f9fa;
    }

    .badge {
      display: inline-block;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.75rem;
      font-weight: 600;
    }

    .badge-blue {
      background: #dbeafe;
      color: #1e40af;
    }
    .badge-green {
      background: #dcfce7;
      color: #166534;
    }
    .badge-orange {
      background: #fed7aa;
      color: #9a3412;
    }
    .badge-red {
      background: #fee2e2;
      color: #991b1b;
    }
    .badge-purple {
      background: #e9d5ff;
      color: #6b21a8;
    }

    ul {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }

    li {
      margin: 0.4rem 0;
    }

    code {
      background: #f1f5f9;
      padding: 0.15rem 0.4rem;
      border-radius: 0.25rem;
      font-family: monospace;
      font-size: 0.9em;
    }

    h3 {
      margin: 1.25rem 0 0.75rem 0;
      font-size: 1.1rem;
      color: #374151;
    }

    h3:first-child {
      margin-top: 0;
    }

    p {
      margin: 0.5rem 0;
      line-height: 1.6;
    }

    .quick-ref {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 0.75rem;
      margin: 0.75rem 0;
    }

    .quick-ref-item {
      background: #f8f9fa;
      padding: 0.75rem;
      border-radius: 0.5rem;
      border-left: 3px solid;
    }

    .quick-ref-item.light {
      border-color: #6ee7b7;
    }
    .quick-ref-item.moderate {
      border-color: #fbbf24;
    }
    .quick-ref-item.vigorous {
      border-color: #f87171;
    }

    .quick-ref-item strong {
      display: block;
      margin-bottom: 0.25rem;
    }

    @media (max-width: 640px) {
      .help-header {
        padding: 1.5rem 1rem;
      }

      .help-header h1 {
        font-size: 1.5rem;
      }

      .toc-grid {
        grid-template-columns: repeat(3, 1fr);
      }

      .section-header h2 {
        font-size: 1.1rem;
      }

      .formula {
        font-size: 0.85rem;
        overflow-x: auto;
      }
    }

    .guest-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem;
      background: white;
      border-bottom: 1px solid #e9ecef;
      position: sticky;
      top: 0;
      z-index: 100;
    }

    .guest-header .logo {
      font-size: 1.25rem;
      font-weight: 700;
      color: #3a9bc0;
    }

    .guest-wrapper {
      min-height: 100vh;
      background: #f8f9fa;
    }
  `;

  private scrollToSection(id: string) {
    this.activeSection = id;
    const el = this.shadowRoot?.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  private handleBackToLogin() {
    this.dispatchEvent(new CustomEvent("back-to-login", { bubbles: true }));
  }

  render() {
    if (this.guestMode) {
      return html`
        <div class="guest-wrapper">
          <div class="guest-header">
            <span class="logo">DAFNE Log</span>
            <wa-button variant="text" @click=${this.handleBackToLogin}>
              <wa-icon slot="prefix" name="arrow-left"></wa-icon>
              Back to Login
            </wa-button>
          </div>
          <div class="help-container">${this.renderHelpContent()}</div>
        </div>
      `;
    }

    return html`
      <app-shell pageTitle="Help">
        <div class="help-container">${this.renderHelpContent()}</div>
      </app-shell>
    `;
  }

  private renderHelpContent() {
    return html`
      <div class="help-header">
        <h1>📘 DAFNE Quick Reference</h1>
        <p>Dose Adjustment For Normal Eating</p>
      </div>

      <!-- Glossary / Sample Log -->
      <div
        class="section"
        style="background: linear-gradient(135deg, #1e293b, #334155); color: white;"
      >
        <div class="section-content" style="padding: 1.5rem;">
          <h3
            style="color: white; margin-top: 0; margin-bottom: 1rem; display: flex; align-items: center; gap: 0.5rem;"
          >
            <wa-icon name="table"></wa-icon> Log Columns Explained
          </h3>
          <p style="color: #94a3b8; margin-bottom: 1rem;">
            Each entry in your DAFNE log uses these columns:
          </p>

          <!-- Sample Log Row -->
          <div
            style="background: white; border-radius: 0.5rem; padding: 0.75rem; margin-bottom: 1rem; overflow-x: auto;"
          >
            <table style="margin: 0; font-size: 0.8rem;">
              <tr style="background: #f1f5f9;">
                <th
                  style="padding: 0.5rem; text-align: center; border-bottom: 2px solid #e2e8f0;"
                >
                  Period
                </th>
                <th
                  style="padding: 0.5rem; text-align: center; border-bottom: 2px solid #e2e8f0;"
                >
                  Time
                </th>
                <th
                  style="padding: 0.5rem; text-align: center; border-bottom: 2px solid #e2e8f0;"
                >
                  GL
                </th>
                <th
                  style="padding: 0.5rem; text-align: center; border-bottom: 2px solid #e2e8f0;"
                >
                  CP
                </th>
                <th
                  style="padding: 0.5rem; text-align: center; border-bottom: 2px solid #e2e8f0;"
                >
                  Quick
                </th>
                <th
                  style="padding: 0.5rem; text-align: center; border-bottom: 2px solid #e2e8f0;"
                >
                  Long
                </th>
              </tr>
              <tr>
                <td
                  style="padding: 0.5rem; text-align: center; font-weight: 600; color: #0d6efd;"
                >
                  B
                </td>
                <td style="padding: 0.5rem; text-align: center;">07:30</td>
                <td
                  style="padding: 0.5rem; text-align: center; color: #059669; font-weight: 500;"
                >
                  6.2
                </td>
                <td
                  style="padding: 0.5rem; text-align: center; color: #d97706; font-weight: 500;"
                >
                  4
                </td>
                <td
                  style="padding: 0.5rem; text-align: center; color: #dc2626; font-weight: 500;"
                >
                  12+2
                </td>
                <td
                  style="padding: 0.5rem; text-align: center; color: #7c3aed; font-weight: 500;"
                >
                  18
                </td>
              </tr>
            </table>
          </div>

          <!-- Glossary Grid -->
          <div
            style="display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 0.75rem;"
          >
            <div
              style="background: rgba(255,255,255,0.1); padding: 0.75rem; border-radius: 0.5rem; border-left: 3px solid #0d6efd;"
            >
              <div style="font-weight: 600; color: #93c5fd;">Period</div>
              <div style="font-size: 0.85rem; color: #cbd5e1;">
                B, L, EM, BT, 3AM (from CGM overnight readings)
              </div>
            </div>
            <div
              style="background: rgba(255,255,255,0.1); padding: 0.75rem; border-radius: 0.5rem; border-left: 3px solid #059669;"
            >
              <div style="font-weight: 600; color: #6ee7b7;">GL</div>
              <div style="font-size: 0.85rem; color: #cbd5e1;">
                Glucose Level in mmol/L (blood sugar reading)
              </div>
            </div>
            <div
              style="background: rgba(255,255,255,0.1); padding: 0.75rem; border-radius: 0.5rem; border-left: 3px solid #d97706;"
            >
              <div style="font-weight: 600; color: #fcd34d;">CP</div>
              <div style="font-size: 0.85rem; color: #cbd5e1;">
                Carb Portions. 1 CP = 10g carbs
              </div>
            </div>
            <div
              style="background: rgba(255,255,255,0.1); padding: 0.75rem; border-radius: 0.5rem; border-left: 3px solid #dc2626;"
            >
              <div style="font-weight: 600; color: #fca5a5;">Quick</div>
              <div style="font-size: 0.85rem; color: #cbd5e1;">
                Quick-acting insulin (QA). Format: base+correction (e.g., 12+2)
              </div>
            </div>
            <div
              style="background: rgba(255,255,255,0.1); padding: 0.75rem; border-radius: 0.5rem; border-left: 3px solid #7c3aed;"
            >
              <div style="font-weight: 600; color: #c4b5fd;">Long</div>
              <div style="font-size: 0.85rem; color: #cbd5e1;">
                Long-acting insulin.
              </div>
            </div>
            <div
              style="background: rgba(255,255,255,0.1); padding: 0.75rem; border-radius: 0.5rem; border-left: 3px solid #f59e0b;"
            >
              <div style="font-weight: 600; color: #fde68a;">Ket</div>
              <div style="font-size: 0.85rem; color: #cbd5e1;">
                Ketones level in mmol/L (check when GL is high, especially
                &ge;15, or if unwell)
              </div>
            </div>
          </div>

          <div
            style="margin-top: 1rem; padding: 0.75rem; background: rgba(255,255,255,0.05); border-radius: 0.5rem; font-size: 0.85rem;"
          >
            <strong style="color: #fbbf24;">Common Aliases:</strong>
            <span style="color: #cbd5e1;">
              BG = GL (blood glucose = glucose level) • QA = Quick • BI = Long
              (background insulin)</span
            >
          </div>
        </div>
      </div>

      <div class="toc">
        <h3>Jump to Section</h3>
        <div class="toc-grid">
          <div
            class="toc-item overview ${this.activeSection === "overview"
              ? "active"
              : ""}"
            @click=${() => this.scrollToSection("overview")}
          >
            What is DAFNE?
          </div>
          <div
            class="toc-item carbs ${this.activeSection === "carbs"
              ? "active"
              : ""}"
            @click=${() => this.scrollToSection("carbs")}
          >
            Carb Counting
          </div>
          <div
            class="toc-item insulin ${this.activeSection === "insulin"
              ? "active"
              : ""}"
            @click=${() => this.scrollToSection("insulin")}
          >
            Insulin Dosing
          </div>
          <div
            class="toc-item correction ${this.activeSection === "correction"
              ? "active"
              : ""}"
            @click=${() => this.scrollToSection("correction")}
          >
            Corrections
          </div>
          <div
            class="toc-item periods ${this.activeSection === "periods"
              ? "active"
              : ""}"
            @click=${() => this.scrollToSection("periods")}
          >
            Meal Periods
          </div>
          <div
            class="toc-item exercise ${this.activeSection === "exercise"
              ? "active"
              : ""}"
            @click=${() => this.scrollToSection("exercise")}
          >
            Exercise
          </div>
          <div
            class="toc-item hypo ${this.activeSection === "hypo"
              ? "active"
              : ""}"
            @click=${() => this.scrollToSection("hypo")}
          >
            Hypo/Hyper
          </div>
          <div
            class="toc-item sick ${this.activeSection === "sick"
              ? "active"
              : ""}"
            @click=${() => this.scrollToSection("sick")}
          >
            Sick Days
          </div>
          <div
            class="toc-item notation ${this.activeSection === "notation"
              ? "active"
              : ""}"
            @click=${() => this.scrollToSection("notation")}
          >
            Note Shortcuts
          </div>
          <div
            class="toc-item software ${this.activeSection === "software"
              ? "active"
              : ""}"
            @click=${() => this.scrollToSection("software")}
          >
            How This App Helps
          </div>
          <div
            class="toc-item about ${this.activeSection === "about"
              ? "active"
              : ""}"
            @click=${() => this.scrollToSection("about")}
          >
            About
          </div>
        </div>
      </div>

      <!-- Overview Section -->
      <div id="overview" class="section overview">
        <div class="section-header">
          <wa-icon name="book"></wa-icon>
          <h2>What is DAFNE?</h2>
        </div>
        <div class="section-content">
          <div class="warning-box" style="margin-bottom: 1.5rem;">
            <h4>⚠️ Important Disclaimer</h4>
            <p>
              This app is
              <strong
                >not affiliated with, endorsed by, or officially connected
                to</strong
              >
              the OzDAFNE program or Diabetes Victoria. It is an independent
              tool built by a DAFNE graduate to help implement DAFNE principles
              in daily diabetes management.
            </p>
            <p style="margin-top: 0.75rem;">For official DAFNE education:</p>
            <ul style="margin: 0.5rem 0; padding-left: 1.5rem;">
              <li>
                <strong>Australia:</strong>
                <a
                  href="https://www.diabetesvic.org.au/support-services/educational-programs/ozdafne/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style="color: #0d6efd; text-decoration: underline;"
                  >OzDAFNE Program (Diabetes Victoria)</a
                >
              </li>
              <li>
                <strong>New Zealand:</strong>
                <a
                  href="https://www.healthpoint.co.nz/public/endocrinology/diabetes-centre-auckland-te-toka-tumai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style="color: #0d6efd; text-decoration: underline;"
                  >Auckland Diabetes Centre</a
                >
              </li>
            </ul>
            <p style="margin-top: 0.75rem;">
              For app support or feedback:
              <a
                href="mailto:kyleyharris+ozdafnesupport@gmail.com"
                style="color: #0d6efd; text-decoration: underline;"
                >email developer</a
              >
            </p>
          </div>

          <p>
            <strong>DAFNE (Dose Adjustment For Normal Eating)</strong> is a
            structured education program that teaches people with Type 1
            diabetes how to estimate carbohydrates, match insulin doses to food
            intake, and make adjustments for exercise and other factors.
          </p>

          <div class="key-point blue">
            <strong>Core Principle:</strong> Match your quick-acting insulin to
            the carbohydrates you eat, using your personal insulin-to-carb
            ratio.
          </div>

          <h3>Key Concepts</h3>
          <table>
            <tr>
              <th>Log Column</th>
              <th>Also Known As</th>
              <th>Meaning</th>
            </tr>
            <tr>
              <td><span class="badge badge-green">GL</span></td>
              <td>BG, blood glucose</td>
              <td>Glucose Level in mmol/L</td>
            </tr>
            <tr>
              <td><span class="badge badge-orange">CP</span></td>
              <td>carbs</td>
              <td>Carbohydrate Portions (1 CP = 10g carbs)</td>
            </tr>
            <tr>
              <td><span class="badge badge-red">Quick</span></td>
              <td>QA, bolus</td>
              <td>Quick-Acting insulin for meals</td>
            </tr>
            <tr>
              <td><span class="badge badge-purple">Long</span></td>
              <td>BI, Long-acting</td>
              <td>Long-acting Background Insulin</td>
            </tr>
            <tr>
              <td><span class="badge badge-blue">CR</span></td>
              <td>correction</td>
              <td>Correction Ratio (units per mmol/L above target)</td>
            </tr>
          </table>
        </div>
      </div>

      <!-- Carbs Section -->
      <div id="carbs" class="section carbs">
        <div class="section-header">
          <wa-icon name="cup-hot"></wa-icon>
          <h2>Carbohydrate Counting</h2>
        </div>
        <div class="section-content">
          <div class="key-point orange">
            <strong
              >1 CP (Carbohydrate Portion) = 10 grams of carbohydrate</strong
            >
          </div>

          <h3>How to Count Carbs</h3>
          <ul>
            <li>Check food labels for "Carbohydrate" per serving</li>
            <li>Use apps or reference books for unlabelled foods</li>
            <li>Estimate portions by weight or visual comparison</li>
            <li>Round to nearest 0.5 CP for practical dosing</li>
          </ul>

          <h3>Common Foods Reference</h3>
          <table>
            <tr>
              <th>Food</th>
              <th>Amount</th>
              <th>Carbs</th>
            </tr>
            <tr>
              <td>Bread (white/wholemeal)</td>
              <td>1 slice (30g)</td>
              <td>~15g (1.5 CP)</td>
            </tr>
            <tr>
              <td>Rice (cooked)</td>
              <td>150g</td>
              <td>~40g (4 CP)</td>
            </tr>
            <tr>
              <td>Pasta (cooked)</td>
              <td>180g</td>
              <td>~50g (5 CP)</td>
            </tr>
            <tr>
              <td>Banana (medium)</td>
              <td>1 whole</td>
              <td>~25g (2.5 CP)</td>
            </tr>
            <tr>
              <td>Apple (medium)</td>
              <td>1 whole</td>
              <td>~15g (1.5 CP)</td>
            </tr>
            <tr>
              <td>Milk</td>
              <td>250ml</td>
              <td>~12g (1 CP)</td>
            </tr>
          </table>

          <div class="info-box">
            <h4>💡 Tip: Protein & Fat</h4>
            <p>
              High-protein or high-fat meals (steak, pizza, fried foods) may
              cause delayed GL rises 2-4 hours later. Consider split dosing or
              adding extra insulin for protein.
            </p>
          </div>
        </div>
      </div>

      <!-- Insulin Section -->
      <div id="insulin" class="section insulin">
        <div class="section-header">
          <wa-icon name="droplet"></wa-icon>
          <h2>Insulin Dosing</h2>
        </div>
        <div class="section-content">
          <h3>Quick-Acting Insulin</h3>
          <p>
            Taken with meals to cover the carbohydrates you eat. Shown as
            <strong>Quick</strong> in your log (also called QA).
          </p>

          <div class="formula">
            <span class="highlight">Meal Dose</span> = CP count × Units per
            CP<br />
            <span class="comment">// Example: 4 CP × 3u/CP = 12 units</span>
          </div>

          <div class="key-point red">
            <strong>Your ratio may differ by meal!</strong> Many people need
            more insulin at breakfast than dinner due to dawn phenomenon.
          </div>

          <h3>Long-Acting Insulin</h3>
          <p>
            Background insulin taken once or twice daily to cover your body's
            baseline needs. Shown as <strong>Long</strong> in your log (also
            called BI or basal).
          </p>

          <div class="info-box">
            <h4>Checking Your Long (BI) Dose</h4>
            <ul>
              <li>Skip a meal and check GL hourly for 4-6 hours</li>
              <li>If GL rises steadily → Long/BI may be too low</li>
              <li>If GL drops → Long/BI may be too high</li>
              <li>Stable GL (±1 mmol/L) → Long/BI is about right</li>
            </ul>
          </div>

          <h3>CP Ratios by Period</h3>
          <p>Configure your ratios in Settings. Common patterns:</p>
          <table>
            <tr>
              <th>Period</th>
              <th>Typical Ratio</th>
              <th>Notes</th>
            </tr>
            <tr>
              <td>Breakfast</td>
              <td>1.5-2.5u/CP</td>
              <td>Often highest due to dawn effect</td>
            </tr>
            <tr>
              <td>Lunch</td>
              <td>1-1.5u/CP</td>
              <td>Usually most sensitive time</td>
            </tr>
            <tr>
              <td>Dinner</td>
              <td>1-1.5u/CP</td>
              <td>Varies by individual</td>
            </tr>
          </table>
        </div>
      </div>

      <!-- Correction Section -->
      <div id="correction" class="section correction">
        <div class="section-header">
          <wa-icon name="calculator"></wa-icon>
          <h2>Correction Doses</h2>
        </div>
        <div class="section-content">
          <p>
            When your glucose level (GL) is above target, you may need extra
            insulin to bring it down.
          </p>

          <div class="formula">
            <span class="highlight">Correction</span> = (Current GL − Target) ÷
            Correction Factor<br />
            <span class="comment"
              >// Example: (12 − 6) ÷ 2 = 3 units correction</span
            >
          </div>

          <h3>Correction Factor (ISF)</h3>
          <p>How many mmol/L one unit of insulin will drop your GL.</p>

          <div class="key-point purple">
            <strong>Typical range:</strong> 1 unit drops GL by 1.5 to 4
            mmol/L.<br />
            Test by correcting a high (no food) and checking after 2-4 hours.
          </div>

          <h3>When to Correct</h3>
          <table>
            <tr>
              <th>Situation</th>
              <th>Action</th>
            </tr>
            <tr>
              <td>GL 8-10, about to eat</td>
              <td>Add correction to meal dose</td>
            </tr>
            <tr>
              <td>GL > 10, not eating</td>
              <td>Give correction alone</td>
            </tr>
            <tr>
              <td>GL high before bed</td>
              <td>Correct cautiously (½ dose)</td>
            </tr>
            <tr>
              <td>Recent insulin (< 3 hrs)</td>
              <td>Wait or reduce - insulin on board!</td>
            </tr>
          </table>

          <div class="warning-box">
            <h4>⚠️ Stacking Warning</h4>
            <p>
              Don't correct again within 2-3 hours of your last dose. "Insulin
              stacking" causes hypos. Wait for the first dose to work!
            </p>
          </div>
        </div>
      </div>

      <!-- Periods Section -->
      <div id="periods" class="section periods">
        <div class="section-header">
          <wa-icon name="clock"></wa-icon>
          <h2>Meal Periods</h2>
        </div>
        <div class="section-content">
          <p>
            DAFNE uses period tags to track meals and apply the correct insulin
            ratios.
          </p>

          <table>
            <tr>
              <th>Tag</th>
              <th>Period</th>
              <th>Typical Time</th>
            </tr>
            <tr>
              <td><span class="badge badge-blue">B</span></td>
              <td>Breakfast</td>
              <td>6:00 - 10:00</td>
            </tr>
            <tr>
              <td><span class="badge badge-blue">after-B</span></td>
              <td>After Breakfast</td>
              <td>Post-breakfast snack/correction</td>
            </tr>
            <tr>
              <td><span class="badge badge-green">L</span></td>
              <td>Lunch</td>
              <td>11:00 - 14:00</td>
            </tr>
            <tr>
              <td><span class="badge badge-green">after-L</span></td>
              <td>After Lunch</td>
              <td>Post-lunch snack/correction</td>
            </tr>
            <tr>
              <td><span class="badge badge-orange">EM</span></td>
              <td>Evening Meal (Dinner)</td>
              <td>17:00 - 20:00</td>
            </tr>
            <tr>
              <td><span class="badge badge-orange">after-EM</span></td>
              <td>After Dinner</td>
              <td>Post-dinner snack/correction</td>
            </tr>
            <tr>
              <td><span class="badge badge-purple">BT</span></td>
              <td>Bedtime</td>
              <td>21:00 - 23:00</td>
            </tr>
            <tr>
              <td><span class="badge badge-red">3AM</span></td>
              <td>Night Check</td>
              <td>00:00 - 05:00 (often from CGM)</td>
            </tr>
          </table>

          <div class="info-box">
            <h4>💡 Smart Period Detection</h4>
            <p>
              Set your typical meal times in Settings → Typical Meal Times. The
              app will automatically suggest the right period based on your
              schedule and existing entries.
            </p>
          </div>
        </div>
      </div>

      <!-- Exercise Section -->
      <div id="exercise" class="section exercise">
        <div class="section-header">
          <wa-icon name="bicycle"></wa-icon>
          <h2>Exercise Adjustments</h2>
        </div>
        <div class="section-content">
          <p>
            Exercise increases insulin sensitivity and can cause hypos during or
            after activity.
          </p>

          <h3>General Guidelines</h3>
          <div class="quick-ref">
            <div class="quick-ref-item light">
              <strong>Light Activity</strong>
              Walking, light housework<br />
              <em>Reduce dose by 10-20%</em>
            </div>
            <div class="quick-ref-item moderate">
              <strong>Moderate Activity</strong>
              Cycling, swimming, dancing<br />
              <em>Reduce dose by 25-50%</em>
            </div>
            <div class="quick-ref-item vigorous">
              <strong>Vigorous Activity</strong>
              Running, sports, gym<br />
              <em>Reduce dose by 50-75%</em>
            </div>
          </div>

          <h3>Timing Matters</h3>
          <table>
            <tr>
              <th>When</th>
              <th>Strategy</th>
            </tr>
            <tr>
              <td>Exercise within 2-3 hrs of meal</td>
              <td>Reduce the meal dose</td>
            </tr>
            <tr>
              <td>Exercise later in day</td>
              <td>Extra carbs before/during, reduce next meal</td>
            </tr>
            <tr>
              <td>Evening exercise</td>
              <td>May need to reduce BI or bedtime snack</td>
            </tr>
            <tr>
              <td>Next day after heavy exercise</td>
              <td>Watch for delayed hypos, reduce ratios</td>
            </tr>
          </table>

          <div class="key-point cyan">
            <strong>The "Lag Effect":</strong> Heavy exercise can lower BG for
            24-48 hours afterward. Consider reducing your next day's ratios by
            10-20%.
          </div>
        </div>
      </div>

      <!-- Hypo/Hyper Section -->
      <div id="hypo" class="section hypo">
        <div class="section-header">
          <wa-icon name="triangle-exclamation"></wa-icon>
          <h2>Hypo & Hyper Management</h2>
        </div>
        <div class="section-content">
          <h3>Hypoglycaemia (Low BG)</h3>
          <p>
            <strong>Definition:</strong> Blood glucose below 4.0 mmol/L (or 72
            mg/dL)
          </p>

          <div class="warning-box">
            <h4>⚠️ Rule of 15</h4>
            <ol>
              <li>
                Take 15g fast-acting carbs (glucose tablets, juice, regular
                soda)
              </li>
              <li>Wait 15 minutes</li>
              <li>Recheck BG</li>
              <li>If still < 4, repeat</li>
              <li>
                Once > 4, have a longer-acting snack if next meal > 1 hour
              </li>
            </ol>
          </div>

          <h3>Fast-Acting Carb Options (15g)</h3>
          <ul>
            <li>3-4 glucose tablets</li>
            <li>150ml fruit juice</li>
            <li>150ml regular (non-diet) soft drink</li>
            <li>3 teaspoons sugar/honey</li>
          </ul>

          <h3>Hyperglycaemia (High BG)</h3>
          <p>
            <strong>Definition:</strong> Blood glucose above 10 mmol/L (or 180
            mg/dL)
          </p>

          <div class="info-box">
            <h4>High BG Checklist</h4>
            <ul>
              <li>Did you miss or underdose insulin?</li>
              <li>Did you undercount carbs?</li>
              <li>Are you unwell or stressed?</li>
              <li>Is your insulin expired or stored incorrectly?</li>
              <li>Pump users: Check your infusion site</li>
            </ul>
          </div>

          <div class="key-point yellow">
            <strong>Ketone Check Required:</strong> If BG is persistently high
            (especially &ge;15 mmol/L / 270 mg/dL), or if you feel unwell, check
            ketones. The app may show early ketone reminders from &gt;14 mmol/L.
            If ketones &gt;1.5 with symptoms, this is an emergency.
          </div>
        </div>
      </div>

      <!-- Sick Days Section -->
      <div id="sick" class="section sick">
        <div class="section-header">
          <wa-icon name="thermometer"></wa-icon>
          <h2>Sick Day Rules</h2>
        </div>
        <div class="section-content">
          <div class="warning-box">
            <h4>⚠️ Never Stop Insulin</h4>
            <p>
              Even if you're not eating, you still need your background insulin.
              Illness usually increases insulin needs, not decreases them.
            </p>
          </div>

          <h3>Sick Day Guidelines</h3>
          <ul>
            <li><strong>Test more often</strong> — every 2-4 hours</li>
            <li>
              <strong>Check ketones</strong> if BG is high (especially &ge;15
              mmol/L) or if you feel unwell
            </li>
            <li><strong>Stay hydrated</strong> — water, sugar-free fluids</li>
            <li>
              <strong>Take your BI</strong> — never skip background insulin
            </li>
            <li>
              <strong>Correct highs</strong> — may need 10-20% more insulin
            </li>
            <li>
              <strong>Easy carbs if needed</strong> — soup, crackers, toast
            </li>
          </ul>

          <h3>When to Seek Help</h3>
          <div class="example-box">
            <h4>Contact your diabetes team if:</h4>
            <ul>
              <li>Vomiting that lasts > 2 hours</li>
              <li>Ketones > 1.5 mmol/L</li>
              <li>Unable to keep fluids down</li>
              <li>BG stays > 15 despite corrections</li>
              <li>Confusion or rapid breathing</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Notation Section -->
      <div id="notation" class="section notation">
        <div class="section-header">
          <wa-icon name="code"></wa-icon>
          <h2>Note Shortcuts</h2>
        </div>
        <div class="section-content">
          <p>
            When importing from LibreView, add these shortcuts to your notes for
            automatic parsing.
          </p>
          <div class="info-box">
            <h4>💡 Why use shortcuts?</h4>
            <p>
              These shortcuts let you quickly log data in LibreView when
              convenient, such as when scanning your sensor. However,
              <strong>we recommend using the DAFNE app first</strong> when
              dosing insulin — it provides valuable predictions, IOB
              calculations, and personalised dose suggestions that LibreView
              cannot offer. Use LibreView shortcuts for quick logging; use the
              DAFNE app for informed dosing decisions.
            </p>
          </div>

          <h3>Exercise Notation</h3>
          <table>
            <tr>
              <th>Shortcut</th>
              <th>Meaning</th>
            </tr>
            <tr>
              <td><code>ex 5 30</code></td>
              <td>Moderate exercise (5/10), 30 minutes</td>
            </tr>
            <tr>
              <td><code>ex 2 15</code></td>
              <td>Light exercise (2/10), 15 minutes</td>
            </tr>
            <tr>
              <td><code>ex 9 45</code></td>
              <td>Vigorous exercise (9/10), 45 minutes</td>
            </tr>
            <tr>
              <td><code>ex easy 20</code></td>
              <td>Light intensity, 20 minutes</td>
            </tr>
            <tr>
              <td><code>ex med 30</code></td>
              <td>Moderate intensity, 30 minutes</td>
            </tr>
            <tr>
              <td><code>ex hard 45</code></td>
              <td>Vigorous intensity, 45 minutes</td>
            </tr>
          </table>
          <p>
            <em
              >Intensity scale: 1-3 = light, 4-7 = moderate, 8-10 = vigorous</em
            >
          </p>

          <h3>Carbs & Insulin</h3>
          <p>
            <em
              >CP and correction (cr) are <strong>optional</strong>. If you only
              record carbs (e.g., "30g" or "3cp"), the app will automatically
              calculate insulin doses using your current ratio settings.</em
            >
          </p>
          <table>
            <tr>
              <th>Shortcut</th>
              <th>Meaning</th>
            </tr>
            <tr>
              <td><code>3.5cp + 2cr</code></td>
              <td>3.5 CP plus 2 units correction</td>
            </tr>
            <tr>
              <td><code>4cp - 1cr</code></td>
              <td>4 CP minus 1 unit correction</td>
            </tr>
            <tr>
              <td><code>30g</code> or <code>30g carbs</code></td>
              <td>30 grams of carbohydrate</td>
            </tr>
            <tr>
              <td><code>7cp = 21u + 1cr</code></td>
              <td>7 CP calculated as 21u base + 1u correction</td>
            </tr>
          </table>

          <h3>Other Shortcuts</h3>
          <table>
            <tr>
              <th>Shortcut</th>
              <th>Meaning</th>
            </tr>
            <tr>
              <td><code>GL 8.5</code> or <code>BG 8.5</code></td>
              <td>Blood glucose reading</td>
            </tr>
            <tr>
              <td><code>ket 0.5</code></td>
              <td>Ketone reading</td>
            </tr>
          </table>

          <div class="info-box">
            <h4>💡 Pro Tip</h4>
            <p>
              You can combine multiple shortcuts in one note:<br />
              <code>ex 5 30 4cp + 1cr GL 7.2</code>
            </p>
          </div>
        </div>
      </div>

      <!-- Software Intelligence Section -->
      <div id="software" class="section software">
        <div class="section-header">
          <wa-icon name="cpu"></wa-icon>
          <h2>How This App Helps</h2>
        </div>
        <div class="section-content">
          <p>
            This section explains how the DAFNE app assists with your diabetes
            management by automating DAFNE calculations and providing
            intelligent recommendations.
          </p>

          <div class="info-box">
            <h4>⚠️ Beta Notice</h4>
            <p>
              This software is in beta and is
              <strong>not affiliated with the official OzDAFNE program</strong>.
              All recommendations should be verified against your OZDAFNE course
              materials. If you notice any calculation errors or unexpected
              behaviour, please
              <a
                href="mailto:kyleyharris+ozdafnesupport@gmail.com"
                style="color: #0d6efd;"
                >email the developer</a
              >.
            </p>
          </div>

          <h3>🧮 Dose Calculations (Add Entry)</h3>
          <p>
            When adding a log entry, the app calculates suggested insulin doses
            using your personal settings:
          </p>
          <table>
            <tr>
              <th>What It Does</th>
              <th>How It Works</th>
            </tr>
            <tr>
              <td><strong>QA Base Dose</strong></td>
              <td>
                Multiplies your CP count by your ratio for the current period
                (e.g., 3 CP × 4u/CP = 12u). Always suggested regardless of IOB.
              </td>
            </tr>
            <tr>
              <td><strong>Correction Dose</strong></td>
              <td>
                Calculates units needed to bring GL to target using your ISF
                (Insulin Sensitivity Factor): (GL − Target) ÷ ISF
              </td>
            </tr>
            <tr>
              <td><strong>IOB Handling (No Stacking)</strong></td>
              <td>
                <strong>Default (DAFNE):</strong> If you have Insulin On Board,
                corrections are not suggested. Always dose for carbs, but wait
                for IOB to clear before adding corrections.
              </td>
            </tr>
            <tr>
              <td><strong>IOB Handling (IOB-Adjusted)</strong></td>
              <td>
                <strong>Optional setting:</strong> Subtracts estimated IOB from
                correction (assumes 4-hour linear decay). May suggest smaller
                corrections while IOB active.
              </td>
            </tr>
          </table>

          <div class="key-point purple">
            <strong>Stacking Setting:</strong> You can choose your IOB behaviour
            in Settings → Correction Factor. The DAFNE course recommends "No
            Stacking" to avoid insulin stacking. The IOB calculation uses a
            simple linear decay over 4 hours — actual curves vary by insulin
            type and individual response.
          </div>

          <h3>🏃 Exercise Adjustments</h3>
          <p>When you log exercise, the app provides recommendations:</p>
          <table>
            <tr>
              <th>Recommendation</th>
              <th>Based On</th>
            </tr>
            <tr>
              <td>QA reduction percentage</td>
              <td>
                Exercise intensity and duration (light 15%, moderate 30%,
                vigorous 50% per hour)
              </td>
            </tr>
            <tr>
              <td>Alternative extra carbs</td>
              <td>
                If you prefer not to reduce insulin, suggested carb intake
                instead
              </td>
            </tr>
            <tr>
              <td>Post-exercise awareness</td>
              <td>
                Reminds about delayed hypo risk through the next 6-12 hours (and
                potentially overnight) after harder exercise
              </td>
            </tr>
          </table>

          <div class="key-point cyan">
            <strong>Limitation:</strong> Exercise response is highly individual.
            These percentages are OZDAFNE guidelines and may need adjustment
            based on your experience.
          </div>

          <h3>🤒 Sick Day Notifications</h3>
          <p>
            When you mark a sick day status, the app provides sick day
            management guidance:
          </p>
          <ul>
            <li><strong>Check ketones</strong> reminders when GL is high</li>
            <li>
              <strong>Never skip insulin</strong> warnings, even if not eating
            </li>
            <li>
              <strong>Increased monitoring</strong> prompts (every 2-4 hours)
            </li>
            <li>
              <strong>Seek help</strong> alerts for concerning ketone levels
            </li>
          </ul>

          <div class="key-point yellow">
            <strong>Limitation:</strong> Sick day management is complex and
            individual. The app provides OZDAFNE guidelines but cannot replace
            medical advice for your specific situation.
          </div>

          <h3>� Real-Time Predictions</h3>
          <p>
            The app provides real-time predictions on the Today screen using
            <strong>DAFNE rule-based modelling</strong>. Unlike systems that
            depend on continuous CGM data, our predictions work immediately from
            your diary entries.
          </p>

          <div class="info-box">
            <h4>🎯 DAFNE-First Approach</h4>
            <p>
              Because CGM data may only sync daily or weekly, predictions
              prioritise <strong>DAFNE rules</strong> (carb ratios, insulin
              timing, absorption curves) for forward guidance. CGM data
              primarily supports historical analysis and pattern detection.
            </p>
          </div>

          <h4>Meal Insulin vs Correction Insulin</h4>
          <p>
            The app distinguishes between insulin logged with carbs (meal dose)
            and insulin logged alone (correction dose):
          </p>
          <table>
            <tr>
              <th>Type</th>
              <th>Detection</th>
              <th>Prediction Model</th>
            </tr>
            <tr>
              <td><strong>Meal Insulin</strong></td>
              <td>QA logged with carbs</td>
              <td>
                Models carb rise balanced by insulin - expects GL to stay stable
              </td>
            </tr>
            <tr>
              <td><strong>Correction Insulin</strong></td>
              <td>QA logged alone (no carbs)</td>
              <td>Models GL drop only - warns about hypo risk</td>
            </tr>
          </table>
          <p>
            This prevents false hypo warnings when you've just eaten. Your meal
            insulin will be offset by the carb rise, so the app won't alarm you
            about IOB when you're in a normal post-meal state.
          </p>

          <h4>DAFNE Absorption Curves</h4>
          <ul>
            <li>
              <strong>Carbs:</strong> Peak effect at 30-90 mins, mostly absorbed
              by 2-3 hours
            </li>
            <li>
              <strong>Quick-acting insulin:</strong> Peak effect at 1-2 hours,
              active for 4-5 hours
            </li>
            <li><strong>1 CP (10g carbs):</strong> Raises GL by ~2.5 mmol/L</li>
            <li><strong>1 unit QA:</strong> Lowers GL by ~2.5 mmol/L</li>
          </ul>

          <h4>Live GL Entry</h4>
          <p>
            Tap the <strong>droplet icon</strong> in the header to quickly log
            your current glucose. This is useful when:
          </p>
          <ul>
            <li>You don't have recent CGM data synced</li>
            <li>You want real-time predictions based on a finger-prick test</li>
            <li>Your CGM is warming up or not available</li>
          </ul>
          <p>
            Live GL entries are saved to your data and work alongside or instead
            of CGM readings. They appear on the glucose graph and are used in
            all predictions.
          </p>

          <h4>Prediction Types</h4>
          <table>
            <tr>
              <th>Prediction</th>
              <th>What It Means</th>
            </tr>
            <tr>
              <td>⬇️ <strong>Hypo Risk</strong></td>
              <td>Correction IOB may cause GL to drop below 4 mmol/L</td>
            </tr>
            <tr>
              <td>⚠️ <strong>Stacking</strong></td>
              <td>Multiple correction doses - wait for IOB to clear</td>
            </tr>
            <tr>
              <td>🍞 <strong>Uncovered Carbs</strong></td>
              <td>Carbs logged without insulin - expect GL rise</td>
            </tr>
            <tr>
              <td>📉 <strong>Correction Active</strong></td>
              <td>Recent correction dose - still working</td>
            </tr>
            <tr>
              <td>🏃 <strong>Exercise Effect</strong></td>
              <td>Increased insulin sensitivity from recent activity</td>
            </tr>
            <tr>
              <td>💉 <strong>Missing Long</strong></td>
              <td>Long-acting insulin not yet recorded today</td>
            </tr>
            <tr>
              <td>ℹ️ <strong>Meal Absorbing</strong></td>
              <td>Positive feedback - meal coverage looks balanced</td>
            </tr>
          </table>

          <div class="key-point green">
            <strong>Key Difference:</strong> Hypo warnings are only triggered by
            <strong>correction IOB</strong>, not meal IOB. If you've just eaten
            and taken meal insulin, the app models the expected rise-and-flatten
            effect rather than alarming about the insulin dose.
          </div>

          <h3>�📊 DAFNE Pattern Analysis</h3>
          <p>
            The app analyses your <strong>diary entries</strong> using the exact
            same methodology a DAFNE graduate or clinician would use. This is
            the core feature of the app - a guiding hand to identify patterns
            and suggest insulin adjustments.
          </p>

          <div class="info-box">
            <h4>🎯 The Key DAFNE Principle</h4>
            <p>
              <strong
                >Pre-meal GL tells you about the PREVIOUS period's
                insulin.</strong
              >
            </p>
            <p style="margin-top: 0.5rem;">
              If your GL is consistently high before lunch, the problem isn't
              lunch - it's that your breakfast insulin wasn't enough to cover
              your breakfast carbs.
            </p>
          </div>

          <h4>How DAFNE Analysis Works</h4>
          <table>
            <tr>
              <th>If GL is HIGH at...</th>
              <th>Then Adjust...</th>
            </tr>
            <tr>
              <td><strong>Breakfast (fasting)</strong></td>
              <td>
                Increase <strong>Long-acting (basal)</strong> insulin - your
                overnight coverage isn't enough
              </td>
            </tr>
            <tr>
              <td><strong>Lunch (pre-meal)</strong></td>
              <td>
                Decrease <strong>Breakfast ratio</strong> (more Quick-acting per
                CP) - e.g., 10g:1u → 8g:1u
              </td>
            </tr>
            <tr>
              <td><strong>Evening Meal (pre-meal)</strong></td>
              <td>
                Decrease <strong>Lunch ratio</strong> (more Quick-acting per CP)
              </td>
            </tr>
            <tr>
              <td><strong>Bedtime</strong></td>
              <td>
                Decrease <strong>Evening meal ratio</strong> (more Quick-acting
                per CP)
              </td>
            </tr>
            <tr>
              <td><strong>3AM check</strong></td>
              <td>
                Check <strong>Long-acting dose/timing</strong> - may need
                adjustment or split dosing
              </td>
            </tr>
          </table>

          <table style="margin-top: 1rem;">
            <tr>
              <th>If GL is LOW at...</th>
              <th>Then Adjust...</th>
            </tr>
            <tr>
              <td><strong>Breakfast (fasting)</strong></td>
              <td><strong>Reduce</strong> Long-acting insulin</td>
            </tr>
            <tr>
              <td><strong>Any pre-meal</strong></td>
              <td>
                <strong>Reduce</strong> the previous meal's Quick-acting
                (increase ratio, e.g., 8g:1u → 10g:1u)
              </td>
            </tr>
          </table>

          <h4>The Pattern Rule (2-3 Days)</h4>
          <p>
            The app starts flagging repeated patterns from
            <strong>2 days</strong> at the same period, with stronger confidence
            at <strong>3+ days</strong> in a 7-day window. This prevents
            over-reacting to one-off readings caused by:
          </p>
          <ul>
            <li>Carb counting errors (unusual meals, eating out)</li>
            <li>Atypical activity levels</li>
            <li>Stress or illness</li>
            <li>Forgotten or mistimed doses</li>
          </ul>

          <h4>Excluded ("Explained") Readings</h4>
          <p>
            The app automatically excludes certain readings from pattern
            analysis:
          </p>
          <ul>
            <li><strong>Sick days</strong> - marked in your entries</li>
            <li>
              <strong>Moderate or vigorous exercise</strong> - can cause unusual
              readings
            </li>
            <li>
              <strong>Alcohol notes</strong> - these readings are treated as
              explained and excluded from adjustment patterns
            </li>
            <li>
              <strong>Missed/late insulin notes</strong> - can explain readings
            </li>
            <li>
              <strong>Hypo treatments</strong> - carbs without insulin at low GL
            </li>
            <li>
              <strong>Noted as unusual</strong> - if your notes mention
              "forgot", "guessed", "ate out", etc.
            </li>
          </ul>

          <h4>Target Ranges (OZDAFNE)</h4>
          <ul>
            <li><strong>Target:</strong> 4.0 - 7.0 mmol/L before meals</li>
            <li><strong>Hypo:</strong> Below 4.0 mmol/L</li>
            <li><strong>Above target:</strong> Above 7.0 mmol/L</li>
          </ul>

          <h4>Pattern Severity</h4>
          <table>
            <tr>
              <th>Severity</th>
              <th>Meaning</th>
              <th>Action</th>
            </tr>
            <tr>
              <td>
                <span style="color: #dc3545; font-weight: bold;"
                  >⚠️ Action</span
                >
              </td>
              <td>Strong pattern detected (5+ days or involves hypos)</td>
              <td>Review and consider adjusting now</td>
            </tr>
            <tr>
              <td>
                <span style="color: #ffc107; font-weight: bold;"
                  >📈 Monitor</span
                >
              </td>
              <td>Emerging pattern (2-4 days)</td>
              <td>Keep watching, may need adjustment soon</td>
            </tr>
            <tr>
              <td>
                <span style="color: #198754; font-weight: bold;">✓ Good</span>
              </td>
              <td>80%+ in range, no concerning lows</td>
              <td>No changes needed</td>
            </tr>
          </table>

          <div class="key-point yellow">
            <strong>Important:</strong> Hypo patterns (low readings) are always
            marked as <strong>Action</strong> severity for safety, even with
            only 2 occurrences. Recurring hypos require prompt attention.
          </div>

          <h4>Adjustment Amounts (OZDAFNE Guidance)</h4>
          <ul>
            <li>
              <strong>Ratio changes:</strong> Move by 1-2g per unit (e.g.,
              10g:1u → 8g:1u for more insulin)
            </li>
            <li>
              <strong>Basal changes:</strong> Adjust by 10-20% or 1-2 units
            </li>
            <li>
              <strong>Re-assess:</strong> Wait 2-3 days after any change before
              making further adjustments
            </li>
          </ul>

          <h4>CGM Insights (Separate Section)</h4>
          <p>
            If you import CGM data, the app also provides
            <strong>CGM-based insights</strong>
            including time-in-range, overnight trends, and post-meal spikes.
            These are displayed separately and complement (not replace) the
            DAFNE diary analysis.
          </p>

          <div class="key-point green">
            <strong>Minimum Data Required:</strong> Pattern analysis needs at
            least <strong>2 days</strong> of diary entries for early pattern
            detection. Reliability improves with <strong>3+ days</strong> of
            consistent GL logging at each period.
          </div>

          <h3>🔄 What the App Does NOT Do</h3>
          <p>
            To set appropriate expectations, here's what the app currently does
            not handle:
          </p>
          <ul>
            <li>
              <strong>Pump bolus automation</strong> – No pump bolus wizard or
              automatic dose delivery to pumps
            </li>
            <li>
              <strong>Automatic pump integration</strong> – Cannot send doses to
              pumps or receive pump data
            </li>
            <li>
              <strong>Live CGM streaming</strong> – CGM data must be imported
              (use Live GL button for real-time predictions)
            </li>
            <li>
              <strong>Protein/fat impact</strong> – Calculations focus on carbs
              only, not delayed fat/protein effects
            </li>
            <li>
              <strong>Menstrual cycle adjustments</strong> – Does not track or
              adjust for hormonal variations
            </li>
            <li>
              <strong>Full alcohol modelling</strong> – Delayed alcohol effects
              are not yet fully modelled in predictions (alcohol notes are
              excluded from pattern-adjustment decisions)
            </li>
          </ul>

          <h3>🐛 Report Issues</h3>
          <p>
            If you notice the app making incorrect calculations or suggestions,
            please report them:
          </p>
          <ul>
            <li>Note what you entered and what the app suggested</li>
            <li>Include what you expected based on OZDAFNE rules</li>
            <li>Your feedback helps us improve accuracy for everyone</li>
          </ul>

          <div class="info-box">
            <h4>💡 Remember</h4>
            <p>
              This app implements OZDAFNE rules to assist your decision making,
              but <strong>you are always in control</strong>. The final dosing
              decision is yours, and you should always verify suggestions
              against your DAFNE training and personal experience.
            </p>
            <p
              style="margin-top: 0.75rem; font-size: 0.875rem; color: #6c757d;"
            >
              This app is not affiliated with the official OzDAFNE program.
              Learn more at
              <a
                href="https://www.diabetesvic.org.au/support-services/educational-programs/ozdafne/"
                target="_blank"
                rel="noopener noreferrer"
                style="color: #0d6efd;"
                >diabetesvic.org.au</a
              >.
            </p>
          </div>
        </div>
      </div>

      <!-- About Section -->
      <div id="about" class="section about">
        <div
          class="section-header"
          style="background: linear-gradient(135deg, #6c757d 0%, #495057 100%);"
        >
          <wa-icon name="circle-info"></wa-icon>
          <h2>About ${VERSION_INFO.name}</h2>
        </div>
        <div class="section-content">
          <div style="text-align: center; margin-bottom: 1.5rem;">
            <div style="font-size: 3rem; margin-bottom: 0.5rem;">📔</div>
            <h3 style="margin: 0 0 0.25rem 0; color: #0d6efd;">
              ${VERSION_INFO.name}
            </h3>
            <div
              style="font-family: monospace; color: #6c757d; font-size: 0.9rem;"
            >
              Version ${VERSION_INFO.version}
            </div>
          </div>

          <p style="white-space: pre-line; line-height: 1.6;">
            ${VERSION_INFO.description}
          </p>

          <wa-divider style="margin: 1.5rem 0;"></wa-divider>

          <div style="text-align: center; color: #6c757d; font-size: 0.875rem;">
            <p style="margin: 0;">${VERSION_INFO.copyright}</p>
            <p style="margin: 0.5rem 0 0 0;">
              Built with ❤️ by a DAFNE graduate
            </p>
            <p
              style="margin: 0.5rem 0 0 0; font-family: monospace; font-size: 0.8rem;"
            >
              Build: ${VERSION_INFO.version}
            </p>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "help-page": HelpPage;
  }
}
